import { useCanPerformMutations } from "#hooks/use-can-perform-mutations"
import { useCreateCategory } from "#hooks/use-categories"
import { useI18n } from "#hooks/use-i18n"
import type { ParsedCSVRow } from "#lib/csv-parser"
import { parseDate, parseNumber } from "#lib/csv-parser"
import type { ImportConfig, ImportMapping } from "#lib/indexeddb-import-store"
import type {
  CreateExpenseCategory,
  ExpenseCategory,
} from "@home-finance-manager/shared/types/expense_categories"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import * as React from "react"
import { Dialog, DialogActions } from "../Dialog"
import { Button } from "../ui/button"
import { Calendar } from "../ui/calendar"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"

const NONE_VALUE = "__none__"

interface RowEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: ParsedCSVRow
  rowIndex: number
  editedData: Partial<ParsedCSVRow>
  mapping: ImportMapping
  config: ImportConfig
  categories: ExpenseCategory[]
  categoryMappings: Map<string, number | "new">
  onSave: (data: Partial<ParsedCSVRow>) => void
  onCategoryMappingChange?: (categoryName: string, categoryId: number | "new") => void
  onCategoriesChange?: (categories: ExpenseCategory[]) => void
}

export function RowEditDialog({
  open,
  onOpenChange,
  row,
  rowIndex,
  editedData,
  mapping,
  config,
  categories,
  categoryMappings,
  onSave,
  onCategoryMappingChange,
  onCategoriesChange,
}: RowEditDialogProps) {
  const { t } = useI18n()
  const canPerformMutations = useCanPerformMutations()
  const [formData, setFormData] = React.useState<Partial<ParsedCSVRow>>(editedData)
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<number | null>(null)
  const [showCreateCategory, setShowCreateCategory] = React.useState(false)
  const [newCategoryName, setNewCategoryName] = React.useState("")
  const [newCategoryColor, setNewCategoryColor] = React.useState("")
  const createCategory = useCreateCategory()

  React.useEffect(() => {
    setFormData(editedData)
    if (!open) {
      setSelectedCategoryId(null)
      return
    }
    if (selectedCategoryId === null) {
      const categoryColumn = Array.isArray(mapping.category)
        ? mapping.category[0]
        : mapping.category || ""
      if (categoryColumn) {
        const mergedRow = { ...row, ...editedData }
        const categoryName = mergedRow[categoryColumn]?.trim()
        if (categoryName) {
          const cat = categories.find((c) => c.name === categoryName)
          if (cat) {
            setSelectedCategoryId(cat.id)
          } else {
            const mappingId = categoryMappings.get(categoryName)
            if (mappingId && typeof mappingId === "number") {
              setSelectedCategoryId(mappingId)
            }
          }
        }
      }
    }
  }, [editedData, open, row, mapping.category, categories, categoryMappings, selectedCategoryId])

  const mergedRow = { ...row, ...formData }

  const nameColumns = Array.isArray(mapping.name) ? mapping.name : [mapping.name || ""]
  const amountColumn = mapping.amount || ""
  const dateColumn = Array.isArray(mapping.date) ? mapping.date[0] : mapping.date || ""
  const categoryColumn = Array.isArray(mapping.category)
    ? mapping.category[0]
    : mapping.category || ""

  const handleSave = () => {
    const categoryColumn = Array.isArray(mapping.category)
      ? mapping.category[0]
      : mapping.category || ""

    let finalFormData = { ...formData }
    if (selectedCategoryId !== null && categoryColumn) {
      const cat = categories.find((c) => c.id === selectedCategoryId)
      if (cat) {
        finalFormData[categoryColumn] = cat.name
      }
    }

    const savedCategoryName = finalFormData[categoryColumn]?.trim()

    let categoryIdToUse: number | null = null
    if (selectedCategoryId !== null) {
      categoryIdToUse = selectedCategoryId
    } else if (savedCategoryName) {
      const cat = categories.find((c) => c.name === savedCategoryName)
      if (cat) {
        categoryIdToUse = cat.id
      }
    }

    if (savedCategoryName && categoryIdToUse !== null && onCategoryMappingChange) {
      onCategoryMappingChange(savedCategoryName, categoryIdToUse)
    }

    onSave(finalFormData)
  }

  const missingFields: string[] = []
  const name = nameColumns
    .map((col) => mergedRow[col] || "")
    .filter(Boolean)
    .join(mapping.nameSeparator || " ")
  if (!name) missingFields.push("Name")

  const amountStr = mergedRow[amountColumn] || ""
  const parsedAmount = parseNumber(amountStr, config.decimalSeparator)
  const amount = parsedAmount ? Math.abs(parsedAmount) : null
  if (!amount || amount <= 0) missingFields.push("Amount")

  const dateStr = mergedRow[dateColumn] || ""
  const date = parseDate(dateStr, config.dateFormat)
  if (!date) missingFields.push("Date")

  const categoryName = categoryColumn ? mergedRow[categoryColumn]?.trim() : undefined
  let categoryId: number | "new" | undefined = undefined
  if (selectedCategoryId !== null) {
    categoryId = selectedCategoryId
  } else if (categoryName) {
    const cat = categories.find((c) => c.name === categoryName)
    if (cat) {
      categoryId = cat.id
    } else {
      categoryId = categoryMappings.get(categoryName)
    }
  }
  if (!categoryId || (typeof categoryId === "string" && categoryId === "new")) {
    missingFields.push("Category")
  }

  const canSave = missingFields.length === 0

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={t("components.expenseImport.editRow", { index: rowIndex + 1 })}
        description={t("components.expenseImport.fillMissingFields")}
        size="xl"
      >
        <div className="space-y-4 pb-6">
          {missingFields.length > 0 && (
            <div className="p-3 rounded bg-destructive/10 border border-destructive/20">
              <div className="text-sm font-medium text-destructive mb-1">
                {t("components.expenseImport.missingFieldsLabel")}:{" "}
                {missingFields
                  .map((field) =>
                    t(`components.expenseImport.missingFields.${field.toLowerCase()}`)
                  )
                  .join(", ")}
              </div>
            </div>
          )}

          {mapping.name && (
            <div className="space-y-2">
              <Label>
                {t("components.expenseImport.enterName")} *{" "}
                {Array.isArray(mapping.name) && `(${mapping.name.length} columns)`}
              </Label>
              {Array.isArray(mapping.name) ? (
                <div className="space-y-2">
                  {nameColumns.map((col, idx) => (
                    <div key={idx} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {col || t("components.expenseImport.selectColumn")}
                      </Label>
                      <Input
                        value={formData[col] ?? row[col] ?? ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            [col]: e.target.value,
                          }))
                        }}
                        placeholder={t("components.expenseImport.enterValueFor", { column: col })}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Input
                  value={formData[mapping.name] ?? row[mapping.name] ?? ""}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      [mapping.name as string]: e.target.value,
                    }))
                  }}
                  placeholder={t("components.expenseImport.enterName")}
                />
              )}
              {!name && (
                <div className="text-xs text-destructive">
                  {t("components.expenseImport.nameRequired")}
                </div>
              )}
            </div>
          )}

          {mapping.amount && (
            <div className="space-y-2">
              <Label>{t("components.expenseImport.enterAmount")} *</Label>
              <Input
                type="text"
                value={formData[amountColumn] ?? row[amountColumn] ?? ""}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    [amountColumn]: e.target.value,
                  }))
                }}
                placeholder={t("components.expenseImport.enterAmount")}
              />
              {(!amount || amount <= 0) && (
                <div className="text-xs text-destructive">
                  {t("components.expenseImport.amountMustBePositive")}
                </div>
              )}
              {amount && amount > 0 && (
                <div className="text-xs text-muted-foreground">
                  {t("components.expenseImport.parsed")} {amount.toFixed(2)}
                </div>
              )}
            </div>
          )}

          {mapping.date && (
            <div className="space-y-2">
              <Label>{t("components.expenseImport.enterDateLabel")} *</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={formData[dateColumn] ?? row[dateColumn] ?? ""}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      [dateColumn]: e.target.value,
                    }))
                  }}
                  placeholder={t("components.expenseImport.enterDate", {
                    format: config.dateFormat,
                  })}
                  className="flex-1"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="icon" className="shrink-0">
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={
                        date
                          ? date
                          : formData[dateColumn] || row[dateColumn]
                            ? (() => {
                                const parsed = parseDate(
                                  formData[dateColumn] || row[dateColumn] || "",
                                  config.dateFormat
                                )
                                return parsed || undefined
                              })()
                            : undefined
                      }
                      onSelect={(selectedDate) => {
                        if (selectedDate) {
                          const formatted = format(selectedDate, config.dateFormat)
                          setFormData((prev) => ({
                            ...prev,
                            [dateColumn]: formatted,
                          }))
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {!date && (
                <div className="text-xs text-destructive">
                  {t("components.expenseImport.dateMustBeInFormat", {
                    format: config.dateFormat,
                  })}
                </div>
              )}
              {date && (
                <div className="text-xs text-muted-foreground">
                  {t("components.expenseImport.parsed")} {format(date, "yyyy-MM-dd")}
                </div>
              )}
            </div>
          )}

          {mapping.category && (
            <div className="space-y-2">
              <Label>{t("components.expenseImport.selectCategory")} *</Label>
              <Select
                value={
                  showCreateCategory
                    ? NONE_VALUE
                    : selectedCategoryId !== null
                      ? String(selectedCategoryId)
                      : (() => {
                          const formCategoryName = formData[categoryColumn]?.trim()
                          if (formCategoryName) {
                            const cat = categories.find((c) => c.name === formCategoryName)
                            if (cat) return String(cat.id)
                          }
                          if (categoryName && categoryId && typeof categoryId === "number") {
                            return String(categoryId)
                          }
                          return NONE_VALUE
                        })()
                }
                onValueChange={(value) => {
                  if (showCreateCategory) return

                  if (value === NONE_VALUE) {
                    setFormData((prev) => {
                      const newData = { ...prev }
                      delete newData[categoryColumn]
                      return newData
                    })
                    setSelectedCategoryId(null)
                  } else if (value === "__create_new__") {
                    if (canPerformMutations) {
                      setShowCreateCategory(true)
                    }
                  } else {
                    const cat = categories.find((c) => c.id === Number(value))
                    if (cat) {
                      const categoryName = cat.name
                      setSelectedCategoryId(cat.id)
                      setFormData((prev) => ({
                        ...prev,
                        [categoryColumn]: categoryName,
                      }))
                      if (onCategoryMappingChange) {
                        onCategoryMappingChange(categoryName, cat.id)
                      }
                    }
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("components.expenseImport.selectCategory")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>{t("common.none")}</SelectItem>
                  <SelectItem value="__create_new__">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border-2 border-dashed border-muted-foreground/50 flex-shrink-0" />
                      <span>{t("components.expenseImport.createNewCategory")}</span>
                    </div>
                  </SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: cat.color,
                            boxShadow: `0 0 4px ${cat.color}40`,
                          }}
                        />
                        <span>{cat.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!categoryId || (typeof categoryId === "string" && categoryId === "new")) && (
                <div className="text-xs text-destructive">
                  {t("components.expenseImport.categoryRequired")}
                </div>
              )}
            </div>
          )}

          {mapping.description && (
            <div className="space-y-2">
              <Label>{t("components.expenseImport.enterDescription")}</Label>
              {Array.isArray(mapping.description) ? (
                <div className="space-y-2">
                  {mapping.description.map((col, idx) => (
                    <div key={idx} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {col || t("components.expenseImport.selectColumn")}
                      </Label>
                      <Input
                        value={formData[col] ?? row[col] ?? ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            [col]: e.target.value,
                          }))
                        }}
                        placeholder={t("components.expenseImport.enterValueFor", { column: col })}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Input
                  value={formData[mapping.description] ?? row[mapping.description] ?? ""}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      [mapping.description as string]: e.target.value,
                    }))
                  }}
                  placeholder={t("components.expenseImport.enterDescription")}
                />
              )}
            </div>
          )}
        </div>

        <DialogActions
          onCancel={() => onOpenChange(false)}
          onConfirm={canSave ? handleSave : undefined}
          confirmText={t("components.expenseImport.save")}
          cancelText={t("components.expenseImport.cancel")}
          confirmVariant="default"
        />
      </Dialog>

      <Dialog
        open={showCreateCategory}
        onOpenChange={setShowCreateCategory}
        title={t("pages.categories.createDialog.title")}
        description={t("pages.categories.createDialog.description")}
        size="md"
      >
        <div className="space-y-4 pb-6">
          <div className="space-y-2">
            <Label htmlFor="new-category-name">{t("pages.categories.form.name")} *</Label>
            <Input
              id="new-category-name"
              type="text"
              placeholder={t("pages.categories.form.namePlaceholder")}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t("pages.categories.form.color")}{" "}
              <span className="text-muted-foreground text-sm">
                {t("pages.categories.form.colorOptional")}
              </span>
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("pages.categories.form.colorDescription")}
            </p>
            <Input
              type="color"
              value={newCategoryColor || "#3B82F6"}
              onChange={(e) => setNewCategoryColor(e.target.value)}
              className="w-full h-12 cursor-pointer"
            />
            {newCategoryColor && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setNewCategoryColor("")}
              >
                {t("pages.categories.form.clear")}
              </Button>
            )}
          </div>
        </div>

        <DialogActions
          onCancel={() => {
            setShowCreateCategory(false)
            setNewCategoryName("")
            setNewCategoryColor("")
          }}
          onConfirm={async () => {
            if (!newCategoryName.trim()) return

            try {
              const categoryData: CreateExpenseCategory = {
                name: newCategoryName.trim(),
                description: "",
                color:
                  newCategoryColor && newCategoryColor.trim() !== "" ? newCategoryColor : undefined,
              }
              const newCategory = await createCategory.mutateAsync(categoryData)

              if (onCategoriesChange) {
                onCategoriesChange([...categories, newCategory])
              }

              const categoryColumn = Array.isArray(mapping.category)
                ? mapping.category[0]
                : mapping.category || ""
              setFormData((prev) => ({
                ...prev,
                [categoryColumn]: newCategory.name,
              }))
              setSelectedCategoryId(newCategory.id)
              if (onCategoryMappingChange) {
                onCategoryMappingChange(newCategory.name, newCategory.id)
              }

              setShowCreateCategory(false)
              setNewCategoryName("")
              setNewCategoryColor("")
            } catch (error) {
              console.error("Failed to create category:", error)
            }
          }}
          confirmText={t("pages.categories.createDialog.confirmText")}
          cancelText={t("components.dialog.cancel")}
          confirmVariant="default"
          isLoading={createCategory.isPending}
          isConfirmDisabled={!canPerformMutations}
          confirmTooltip={
            !canPerformMutations ? t("components.expenses.tooltips.actionDisabled") : undefined
          }
        />
      </Dialog>
    </>
  )
}
